/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel } from '../../../../../core/models';
import { NamespaceService } from '../../services/utils';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-namespace',
    templateUrl: './namespace.component.html',
    styleUrls: ['./namespace.component.scss'],
})

export class NamespaceComponent extends BaseComponent {
    /**
     * List of items searched for in the namespace dropdown.
     */
    public namespaceSearch: any[] = [];

    /**
     * Selected namespace.
     */
    public nameSpaceSelected: DropdownModel;

    /**
     * The ability to disable the dropdown externally via the service.
     */
    public disabled: boolean = false;

    /**
     * Store the search term.
     * @private
     */
    private searchTerm: string = '';

    /**
     * Store namespaces.
     * @private
     */
    private _namespaces: DropdownModel[];

    constructor(
        private namespaceService: NamespaceService,
    ) {
        super();

        this.namespaces = this.namespaceService.namespaces;
        this.nameSpaceSelected = this.namespaceService.selectedNamespace;

        // If there are no namespaces disable the dropdown
        this.disabled = !this.namespaces?.length;

        this.subscribeToNamespaceChanges();
    }

    /**
     * Namespaces setter.
     * @param namespaces
     */
    set namespaces(namespaces: any[]) {
        this._namespaces = namespaces;
        // Poke the namespaces search filter so it updates the list with the latest data.
        this.namespacesSearchContent(this.searchTerm);
    }

    /**
     * Namespaces getter.
     */
    get namespaces(): any[] {
        return this._namespaces;
    }

    /**
     * Search for namespaces dropdown.
     * @param val
     */
    public namespacesSearchContent(val: any): void {
        this.searchTerm = val;
        const regex = new RegExp(val, 'gi');
        const results = [];
        // filter our data
        for (const namespace of this.namespaces) {
            if (namespace.value?.match(regex) || !val) {
                results.push(namespace.value);
            }
        }

        this.namespaceSearch = results;
    }

    /**
     * Set the selected namespace.
     * @param namespace
     */
    public setSelectedNamespace(namespace: any): void {
        this.nameSpaceSelected = namespace;
        this.namespaceService.selectedNamespace = namespace;
    }

    /**
     * Subscribe to namespace changes
     * @private
     */
    private subscribeToNamespaceChanges(): void {
        // Subscribe to selected change.
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any): void => {
                    this.nameSpaceSelected = namespace;
                }
            });

        // Subscribe to list change.
        this.namespaceService.changeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespaces: any[]): void => {
                    this.namespaces = namespaces;
                    this.updateSelectedNamespace(namespaces);
                }
            });

        // Subscribe to list change.
        this.namespaceService.disableEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (status: any): void => {
                    this.disabled = status;
                }
            });
    }

    /**
     * Update selected namespace if selected namespace cannot be found.
     * @param namespaces
     * @private
     */
    private updateSelectedNamespace(namespaces: any[]): void {
        const namespaceExists = namespaces.find(item => item.value === this.nameSpaceSelected.value);
        if (!namespaceExists) {
            this.nameSpaceSelected = namespaces.find(item => item.value === 'All');
        }
    }
}

